import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Button, Card, Input, Space, Tabs, Typography, message, Modal, Segmented, List } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, ReloadOutlined, EditOutlined, PlayCircleOutlined, PauseCircleOutlined, PictureOutlined, AudioOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import JsonEditor from '../../JsonEditor/JsonEditor';
import { TAIDatasetLessonConfig, TAIDatasetEntry, createEmptyAIDatasetLessonConfig } from '../../../models/courses/aiDataset';
import { useObjectsApi } from '../../../api/objects/objects';
import type { TImage, TSound } from '../../../models/objects/media';
import './AIDatasetLessonAdmin.css';

const { Title, Text } = Typography;

type LanguageKey = 'fr' | 'en' | 'ar';

interface AIDatasetLessonAdminProps {
  config: TAIDatasetLessonConfig;
  onConfigChange: (config: TAIDatasetLessonConfig) => void;
  onSave: () => void;
  saving?: boolean;
  initialStatement?: string;
  onStatementChange?: (statement: string) => void;
}

const AIDatasetLessonAdmin: React.FC<AIDatasetLessonAdminProps> = ({
  config,
  onConfigChange,
  onSave,
  saving = false,
  initialStatement,
  onStatementChange,
}) => {
  const { t } = useTranslation();
  const [activeLang, setActiveLang] = useState<LanguageKey>('fr');
  const [jsonEditorOpen, setJsonEditorOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [keyTypeByEntry, setKeyTypeByEntry] = useState<Record<string, 'text' | 'image' | 'audio'>>({});

  // Media picker state
  const objectsApi = useObjectsApi();
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaPickerType, setMediaPickerType] = useState<'image' | 'audio'>('image');
  const [mediaPickerTargetKeyId, setMediaPickerTargetKeyId] = useState<string | null>(null);
  const [mediaPickerContext, setMediaPickerContext] = useState<'key' | 'feature' | 'noise'>('key');
  const [mediaFeatureTargetLang, setMediaFeatureTargetLang] = useState<LanguageKey | null>(null);
  const [mediaNoiseTargetLang, setMediaNoiseTargetLang] = useState<LanguageKey | null>(null);
  const [images, setImages] = useState<TImage[]>([]);
  const [sounds, setSounds] = useState<TSound[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaSearch, setMediaSearch] = useState<string>('');
  const filteredImages = useMemo(() => {
    const q = mediaSearch.trim().toLowerCase();
    if (!q) return images;
    return images.filter((img) =>
      (img.file_name || '').toLowerCase().includes(q) || (img.file_path || '').toLowerCase().includes(q)
    );
  }, [images, mediaSearch]);
  const filteredSounds = useMemo(() => {
    const q = mediaSearch.trim().toLowerCase();
    if (!q) return sounds;
    return sounds.filter((s) =>
      (s.file_name || '').toLowerCase().includes(q) || (s.file_path || '').toLowerCase().includes(q)
    );
  }, [sounds, mediaSearch]);

  // Create a stable key based on config content to force remount when config changes
  const configHash = useMemo(() => {
    try {
      return JSON.stringify(config).length + Object.keys(config.dataset || {}).length;
    } catch {
      return 0;
    }
  }, [config]);

  const languages: { key: LanguageKey; label: string }[] = [
    { key: 'fr', label: 'Français' },
    { key: 'en', label: 'English' },
    { key: 'ar', label: 'العربية' },
  ];

  const updateConfig = (updater: (current: TAIDatasetLessonConfig) => TAIDatasetLessonConfig) => {
    const updated = updater(config);
    onConfigChange(updated);
  };

  const detectKeyType = useCallback((value: string | undefined): 'text' | 'image' | 'audio' => {
    if (!value) return 'text';
    const v = value.toLowerCase();
    if (/\.(png|jpe?g|gif|svg|webp)$/.test(v) || v.includes('/images/') || v.includes('/uploads/')) return 'image';
    if (/\.(mp3|wav|ogg|m4a)$/.test(v) || v.includes('/sounds/') || v.includes('/audio/')) return 'audio';
    return 'text';
  }, []);

  const detectFeatureType = useCallback((value: string | undefined): 'text' | 'image' | 'audio' => {
    if (!value) return 'text';
    const v = value.toLowerCase();
    if (/\.(png|jpe?g|gif|svg|webp)$/.test(v) || v.includes('/images/') || v.includes('/uploads/')) return 'image';
    if (/\.(mp3|wav|ogg|m4a)$/.test(v) || v.includes('/sounds/') || v.includes('/audio/')) return 'audio';
    return 'text';
  }, []);

  const handleKeyTypeChange = (keyId: string, type: 'text' | 'image' | 'audio') => {
    setKeyTypeByEntry((prev) => ({ ...prev, [keyId]: type }));
  };

  const openMediaPicker = async (keyId: string, type: 'image' | 'audio', context: 'key' | 'feature' | 'noise' = 'key', lang?: LanguageKey) => {
    setMediaPickerTargetKeyId(keyId);
    setMediaPickerType(type);
    setMediaPickerContext(context);
    setMediaFeatureTargetLang(context === 'feature' ? (lang ?? null) : null);
    setMediaNoiseTargetLang(context === 'noise' ? (lang ?? null) : null);
    setMediaPickerOpen(true);
    setMediaLoading(true);
    try {
      if (type === 'image') {
        const resp = await objectsApi.getImages();
        setImages(resp.data || []);
      } else {
        const resp = await objectsApi.getSounds();
        setSounds(resp.data || []);
      }
    } catch (e: any) {
      message.error(e?.response?.data || e?.message || 'Failed to load media');
    } finally {
      setMediaLoading(false);
    }
  };

  const chooseMedia = (path: string) => {
    if (mediaPickerContext !== 'noise' && !mediaPickerTargetKeyId) return;
    if (mediaPickerContext === 'key') {
      updateConfig((current) => ({
        ...current,
        dataset: {
          ...(current.dataset || {}),
          [mediaPickerTargetKeyId!]: {
            ...(current.dataset?.[mediaPickerTargetKeyId!] || { key: '' }),
            key: path,
          },
        },
      }));
      setKeyTypeByEntry((prev) => ({ ...prev, [mediaPickerTargetKeyId!]: mediaPickerType }));
    } else if (mediaPickerContext === 'feature' && mediaFeatureTargetLang) {
      const featureKey = `features_${mediaFeatureTargetLang}` as keyof TAIDatasetEntry;
      updateConfig((current) => {
        const entry = current.dataset?.[mediaPickerTargetKeyId!] || { key: '' };
        const existing = (entry[featureKey] as string[] | undefined) || [];
        return {
          ...current,
          dataset: {
            ...(current.dataset || {}),
            [mediaPickerTargetKeyId!]: {
              ...entry,
              [featureKey]: [...existing, path],
            },
          },
        } as TAIDatasetLessonConfig;
      });
    } else if (mediaPickerContext === 'noise' && mediaNoiseTargetLang) {
      updateConfig((current) => {
        const bruitKey = `bruit_${mediaNoiseTargetLang}` as keyof TAIDatasetLessonConfig;
        const existing = (current[bruitKey] as string[] | undefined) || [];
        return {
          ...current,
          [bruitKey]: [...existing, path],
        } as TAIDatasetLessonConfig;
      });
    }
    setMediaPickerOpen(false);
    setMediaPickerTargetKeyId(null);
    setMediaFeatureTargetLang(null);
    setMediaNoiseTargetLang(null);
  };

  // Bruit (Noise) Management
  const handleBruitAdd = (lang: LanguageKey, value: string) => {
    if (!value.trim()) return;
    updateConfig((current) => ({
      ...current,
      [`bruit_${lang}`]: [...(current[`bruit_${lang}` as keyof TAIDatasetLessonConfig] as string[] || []), value.trim()],
    }));
  };

  const handleBruitUpdate = (lang: LanguageKey, index: number, value: string) => {
    updateConfig((current) => {
      const bruit = (current[`bruit_${lang}` as keyof TAIDatasetLessonConfig] as string[] || []);
      const updated = [...bruit];
      updated[index] = value.trim();
      return {
        ...current,
        [`bruit_${lang}`]: updated,
      };
    });
  };

  const handleBruitRemove = (lang: LanguageKey, index: number) => {
    updateConfig((current) => {
      const bruit = (current[`bruit_${lang}` as keyof TAIDatasetLessonConfig] as string[] || []);
      return {
        ...current,
        [`bruit_${lang}`]: bruit.filter((_, i) => i !== index),
      };
    });
  };

  // Dataset Entry Management
  const handleAddEntry = () => {
    if (!newKeyName.trim()) {
      message.warning(t('aiDataset.admin.messages.keyRequired') || 'Key name is required');
      return;
    }
    const keyId = newKeyName.trim().toLowerCase().replace(/\s+/g, '_');
    if (config.dataset?.[keyId]) {
      message.warning(t('aiDataset.admin.messages.keyExists') || 'Key already exists');
      return;
    }
    updateConfig((current) => ({
      ...current,
      dataset: {
        ...(current.dataset || {}),
        [keyId]: {
          key: newKeyName.trim(),
          [`features_${activeLang}`]: [],
        },
      },
    }));
    setNewKeyName('');
    message.success(t('aiDataset.admin.messages.entryAdded') || 'Entry added successfully');
  };

  const handleRemoveEntry = (keyId: string) => {
    Modal.confirm({
      title: t('aiDataset.admin.messages.confirmDelete') || 'Confirm Delete',
      content: t('aiDataset.admin.messages.confirmDeleteContent') || 'Are you sure you want to delete this entry?',
      onOk: () => {
        updateConfig((current) => {
          const { [keyId]: removed, ...rest } = current.dataset || {};
          return {
            ...current,
            dataset: rest,
          };
        });
        message.success(t('aiDataset.admin.messages.entryDeleted') || 'Entry deleted successfully');
      },
    });
  };

  const handleEntryKeyUpdate = (keyId: string, newKey: string) => {
    updateConfig((current) => ({
      ...current,
      dataset: {
        ...(current.dataset || {}),
        [keyId]: {
          ...(current.dataset?.[keyId] || { key: '' }),
          key: newKey.trim(),
        },
      },
    }));
  };

  // Features Management
  const handleFeatureAdd = (keyId: string, lang: LanguageKey, value: string) => {
    if (!value.trim()) return;
    updateConfig((current) => {
      const entry = current.dataset?.[keyId] || { key: '' };
      const features = entry[`features_${lang}` as keyof TAIDatasetEntry] as string[] || [];
      return {
        ...current,
        dataset: {
          ...(current.dataset || {}),
          [keyId]: {
            ...entry,
            [`features_${lang}`]: [...features, value.trim()],
          },
        },
      };
    });
  };

  const handleFeatureUpdate = (keyId: string, lang: LanguageKey, index: number, value: string) => {
    updateConfig((current) => {
      const entry = current.dataset?.[keyId] || { key: '' };
      const features = [...(entry[`features_${lang}` as keyof TAIDatasetEntry] as string[] || [])];
      features[index] = value.trim();
      return {
        ...current,
        dataset: {
          ...(current.dataset || {}),
          [keyId]: {
            ...entry,
            [`features_${lang}`]: features,
          },
        },
      };
    });
  };

  const handleFeatureRemove = (keyId: string, lang: LanguageKey, index: number) => {
    updateConfig((current) => {
      const entry = current.dataset?.[keyId] || { key: '' };
      const features = (entry[`features_${lang}` as keyof TAIDatasetEntry] as string[] || []).filter((_, i) => i !== index);
      return {
        ...current,
        dataset: {
          ...(current.dataset || {}),
          [keyId]: {
            ...entry,
            [`features_${lang}`]: features,
          },
        },
      };
    });
  };

  const ArrayEditor: React.FC<{
    items: string[];
    onAdd: (value: string) => void;
    onUpdate: (index: number, value: string) => void;
    onRemove: (index: number) => void;
    placeholder: string;
  }> = ({ items, onAdd, onUpdate, onRemove, placeholder }) => {
    const [newItem, setNewItem] = useState('');

    const renderPreview = (value: string) => {
      const type = detectFeatureType(value);
      if (type === 'image') {
        return <img src={value} alt="feature" className="ai-dataset-array-item-media" />;
      }
      if (type === 'audio') {
        return <AdminAudioPlayer src={value} />;
      }
      return null;
    };

    return (
      <div className="ai-dataset-array-editor">
        {items.map((item, index) => (
          <div key={index} className="ai-dataset-array-item">
            <div className="ai-dataset-array-item-input">
              <Input
                value={item}
                onChange={(e) => onUpdate(index, e.target.value)}
                placeholder={placeholder}
                size="small"
              />
            </div>
            {renderPreview(item)}
            <Button
              icon={<DeleteOutlined />}
              danger
              type="text"
              size="small"
              onClick={() => onRemove(index)}
            />
          </div>
        ))}
        <div className="ai-dataset-array-add-row">
          <Input
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder={placeholder}
            size="small"
            onPressEnter={() => {
              if (newItem.trim()) {
                onAdd(newItem);
                setNewItem('');
              }
            }}
          />
          <Button
            icon={<PlusOutlined />}
            type="primary"
            size="small"
            onClick={() => {
              if (newItem.trim()) {
                onAdd(newItem);
                setNewItem('');
              }
            }}
          >
            {t('aiDataset.admin.add')}
          </Button>
        </div>
      </div>
    );
  };

  const renderBruitEditor = (lang: LanguageKey) => {
    const bruit = (config[`bruit_${lang}` as keyof TAIDatasetLessonConfig] as string[] || []);
    return (
      <>
        <ArrayEditor
          items={bruit}
          onAdd={(value) => handleBruitAdd(lang, value)}
          onUpdate={(index, value) => handleBruitUpdate(lang, index, value)}
          onRemove={(index) => handleBruitRemove(lang, index)}
          placeholder={t('aiDataset.admin.bruitPlaceholder')}
        />
        <Space size="small" style={{ marginTop: 8 }}>
          <Button size="small" icon={<PictureOutlined />} onClick={() => openMediaPicker('noise', 'image', 'noise', lang)}>
            {t('aiDataset.admin.addImageNoise', 'Ajouter image')}
          </Button>
          <Button size="small" icon={<AudioOutlined />} onClick={() => openMediaPicker('noise', 'audio', 'noise', lang)}>
            {t('aiDataset.admin.addAudioNoise', 'Ajouter audio')}
          </Button>
        </Space>
      </>
    );
  };

  const renderFeaturesEditor = (keyId: string, lang: LanguageKey) => {
    const entry = config.dataset?.[keyId];
    const features = (entry?.[`features_${lang}` as keyof TAIDatasetEntry] as string[] || []);
    return (
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <ArrayEditor
          items={features}
          onAdd={(value) => handleFeatureAdd(keyId, lang, value)}
          onUpdate={(index, value) => handleFeatureUpdate(keyId, lang, index, value)}
          onRemove={(index) => handleFeatureRemove(keyId, lang, index)}
          placeholder={t('aiDataset.admin.featurePlaceholder')}
        />
        <Space size="small">
          <Button
            size="small"
            icon={<PictureOutlined />}
            onClick={() => openMediaPicker(keyId, 'image', 'feature', lang)}
          >
            {t('aiDataset.admin.addImageFeature', 'Add image')}
          </Button>
          <Button
            size="small"
            icon={<AudioOutlined />}
            onClick={() => openMediaPicker(keyId, 'audio', 'feature', lang)}
          >
            {t('aiDataset.admin.addAudioFeature', 'Add audio')}
          </Button>
        </Space>
      </Space>
    );
  };

  const AdminAudioPlayer: React.FC<{ src: string }> = ({ src }) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;

      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);
      const handleEnded = () => setIsPlaying(false);

      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('ended', handleEnded);

      return () => {
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('ended', handleEnded);
      };
    }, []);

    const togglePlay = () => {
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
        } else {
          audioRef.current.play().catch(() => {
            setIsPlaying(false);
          });
        }
      }
    };

    return (
      <div className="ai-dataset-admin-audio-preview">
        <audio ref={audioRef} src={src} />
        <button
          type="button"
          className="ai-dataset-admin-audio-play-button"
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <PauseCircleOutlined className="ai-dataset-admin-audio-icon" />
          ) : (
            <PlayCircleOutlined className="ai-dataset-admin-audio-icon" />
          )}
        </button>
      </div>
    );
  };

  return (
    <div className="ai-dataset-admin-container">
      <Card size="small">
        <Title level={5} style={{ margin: 0, marginBottom: 8 }}>{t('aiDataset.admin.title')}</Title>
        <Text type="secondary" style={{ fontSize: 12 }}>{t('aiDataset.admin.description')}</Text>
      </Card>

      <div className="ai-dataset-main-grid">
        {/* Bruit (Noise) Editor */}
        <Card
          size="small"
          title={
            <span className="ai-dataset-section-title" style={{ fontSize: 14 }}>
              {t('aiDataset.admin.bruitTitle')}
            </span>
          }
        >
          <Tabs
            activeKey={activeLang}
            onChange={(key) => setActiveLang(key as LanguageKey)}
            className="ai-dataset-lang-tabs"
            size="small"
            items={languages.map((lang) => ({
              key: lang.key,
              label: lang.label,
              children: renderBruitEditor(lang.key),
            }))}
          />
        </Card>

        {/* Dataset Entries */}
        <Card
          size="small"
          title={
            <span className="ai-dataset-section-title" style={{ fontSize: 14 }}>
              {t('aiDataset.admin.datasetTitle')}
            </span>
          }
        >
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            {/* Add New Entry */}
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder={t('aiDataset.admin.newKeyPlaceholder')}
                onPressEnter={handleAddEntry}
                size="small"
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddEntry} size="small">
                {t('aiDataset.admin.addEntry')}
              </Button>
            </Space.Compact>

            {/* Existing Entries */}
            {Object.keys(config.dataset || {}).length === 0 ? (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', textAlign: 'center', padding: '20px 0' }}>
                {t('aiDataset.admin.noEntries')}
              </Text>
            ) : (
              <div className="ai-dataset-entries-scroll">
                {Object.entries(config.dataset || {}).map(([keyId, entry]) => (
                  <div key={keyId} className="ai-dataset-entry-card">
                    <div className="ai-dataset-entry-header" style={{ gap: 8 }}>
                      <Segmented
                        size="small"
                        value={keyTypeByEntry[keyId] || detectKeyType(entry.key)}
                        onChange={(val) => handleKeyTypeChange(keyId, val as any)}
                        options={[
                          { label: t('Texte') || 'Text', value: 'text' },
                          { label: t('Image') || 'Image', value: 'image' },
                          { label: t('Audio') || 'Audio', value: 'audio' },
                        ]}
                      />
                      {(() => {
                        const type = keyTypeByEntry[keyId] || detectKeyType(entry.key);
                        if (type === 'text') {
                          return (
                            <Input
                              value={entry.key}
                              onChange={(e) => handleEntryKeyUpdate(keyId, e.target.value)}
                              placeholder={t('aiDataset.admin.keyPlaceholder')}
                              size="small"
                              style={{ fontWeight: 600, flex: 1 }}
                            />
                          );
                        }
                        return (
                          <Space.Compact style={{ flex: 1 }} size="small">
                            <Input
                              value={entry.key}
                              readOnly
                              placeholder={type === 'image' ? (t('Chemin image') as any) || 'Image path' : (t('Chemin audio') as any) || 'Audio path'}
                              size="small"
                              style={{ fontWeight: 600 }}
                            />
                            <Button size="small" onClick={() => openMediaPicker(keyId, type)}>
                              {type === 'image' ? (t('Sélectionner image') as any) || 'Select image' : (t('Sélectionner audio') as any) || 'Select audio'}
                            </Button>
                            {entry.key && (
                              <Button size="small" onClick={() => handleEntryKeyUpdate(keyId, '')}>
                                {t('Effacer') || 'Clear'}
                              </Button>
                            )}
                          </Space.Compact>
                        );
                      })()}
                      <Button
                        icon={<DeleteOutlined />}
                        danger
                        type="text"
                        size="small"
                        onClick={() => handleRemoveEntry(keyId)}
                      />
                    </div>

                    {/* Preview for Image or Audio */}
                    {(() => {
                      const type = keyTypeByEntry[keyId] || detectKeyType(entry.key);
                      if (type === 'image' && entry.key) {
                        return (
                          <div className="ai-dataset-admin-media-preview">
                            <img src={entry.key} alt="Preview" className="ai-dataset-admin-image-preview" />
                          </div>
                        );
                      }
                      if (type === 'audio' && entry.key) {
                        return (
                          <AdminAudioPlayer src={entry.key} />
                        );
                      }
                      return null;
                    })()}

                    <Tabs
                      activeKey={activeLang}
                      onChange={(key) => setActiveLang(key as LanguageKey)}
                      className="ai-dataset-lang-tabs"
                      size="small"
                      items={languages.map((lang) => ({
                        key: lang.key,
                        label: lang.label,
                        children: renderFeaturesEditor(keyId, lang.key),
                      }))}
                    />
                  </div>
                ))}
              </div>
            )}
          </Space>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="ai-dataset-actions">
        <Button
          icon={<EditOutlined />}
          onClick={() => {
            // Clear localStorage to ensure fresh config is shown
            if (typeof window !== 'undefined') {
              localStorage.removeItem('ai-dataset-config-editor');
            }
            setJsonEditorOpen(true);
          }}
          size="small"
        >
          {t('aiDataset.admin.openJsonEditor')}
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            Modal.confirm({
              title: t('aiDataset.admin.messages.confirmReset') || 'Confirm Reset',
              content: t('aiDataset.admin.messages.confirmResetContent') || 'Are you sure you want to reset all data?',
              onOk: () => {
                onConfigChange(createEmptyAIDatasetLessonConfig());
                message.success(t('aiDataset.admin.messages.resetSuccess') || 'Configuration reset successfully');
              },
            });
          }}
          size="small"
        >
          {t('aiDataset.admin.reset')}
        </Button>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={onSave}
          loading={saving}
          size="small"
        >
          {t('aiDataset.admin.save')}
        </Button>
      </div>

      {/* JSON Editor Modal */}
      <Modal
        title={t('aiDataset.admin.editorTitle')}
        open={jsonEditorOpen}
        onCancel={() => setJsonEditorOpen(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <JsonEditor<TAIDatasetLessonConfig>
          key={`json-editor-${configHash}`} // Force remount when config changes
          title={t('aiDataset.admin.editorTitle')}
          value={config}
          initialText={JSON.stringify(config, null, 2)} // Always use current config (highest priority)
          showStatement={true}
          initialStatement={initialStatement}
          onStatementChange={onStatementChange}
          onApply={(data) => {
            // Strip statement if present in imported JSON
            const { statement: _stmt, ...rest } = (data || {}) as any;
            onConfigChange(rest as TAIDatasetLessonConfig);
            setJsonEditorOpen(false);
            message.success(t('aiDataset.admin.messages.jsonApplied') || 'Configuration applied successfully');
          }}
          storageKey="ai-dataset-config-editor"
          minRows={20}
        />
      </Modal>

      {/* Media Picker Modal */}
      <Modal
        title={mediaPickerType === 'image' ? (t('Sélectionner une image') as any) || 'Select an image' : (t('Sélectionner un audio') as any) || 'Select an audio'}
        open={mediaPickerOpen}
        onCancel={() => setMediaPickerOpen(false)}
        footer={null}
        width={800}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          <Input.Search
            placeholder={t('Rechercher par nom de fichier') as any}
            allowClear
            value={mediaSearch}
            onChange={(e) => setMediaSearch(e.target.value)}
          />
          {mediaPickerType === 'image' ? (
            <List
              loading={mediaLoading}
              grid={{ gutter: 12, column: 4 }}
              dataSource={filteredImages}
              renderItem={(img) => (
                <List.Item key={img.id}>
                  <Card
                    hoverable
                    size="small"
                    cover={<img src={img.file_path} alt={img.file_name} style={{ height: 120, objectFit: 'cover' }} />}
                    onClick={() => chooseMedia(img.file_path)}
                  >
                    <Typography.Text ellipsis style={{ width: '100%', display: 'block' }}>{img.file_name}</Typography.Text>
                  </Card>
                </List.Item>
              )}
            />
          ) : (
            <List
              loading={mediaLoading}
              dataSource={filteredSounds}
              renderItem={(s) => (
                <List.Item key={s.id} onClick={() => chooseMedia(s.file_path)} style={{ cursor: 'pointer' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Typography.Text strong ellipsis>{s.file_name}</Typography.Text>
                    <audio src={s.file_path} controls style={{ width: '100%' }} />
                  </Space>
                </List.Item>
              )}
            />
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default AIDatasetLessonAdmin;
